import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { usePostCodes } from '../../../Aemter/hooks';
import { PostcodeSearch } from '../../../Clients/AddressTab/PostcodeSearch';
import { ILegalTransactionRelationshipRealEstate } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { IPostcode } from '../../../Interfaces/IPostcode';
import { MapSearch } from './MapSearch/MapSearch';

interface IProps {
  formValues: ILegalTransactionRelationshipRealEstate;
  setFormValues: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationshipRealEstate>
  >;
}

export const RealEstateAddress: React.FC<IProps> = ({
  formValues: {
    Latitude,
    Longitude,
    Street,
    StreetAditional,
    StreetNr,
    idPostcode,
    Postcode,
    City,
  },
  setFormValues,
}) => {
  const { postcodes, isLoadingPostcodes } = usePostCodes();

  const hasCoordinates = useMemo(
    () => !!Latitude || !!Longitude,
    [Latitude, Longitude]
  );

  const postcodeObject: IPostcode | undefined = useMemo(
    () =>
      City && Postcode && idPostcode && idPostcode > 0
        ? {
            idPostcode: idPostcode,
            Postcode: Postcode,
            City: City,
            idCountry: 1,
          }
        : undefined,
    [idPostcode, Postcode, City]
  );

  return (
    <>
      <Typography variant='h5' sx={{ mt: 0, mb: 1 }}>
        Anschrift des Objektes
        <br />
        <Typography>
          Sie müssen entweder die Adresse oder Längen- und Breitengrade angeben.
          Auch beide Informationen können angebeben werden.
        </Typography>
      </Typography>
      <Grid container spacing={2}>
        <Grid item sm={5}>
          <TextField
            label='Straße'
            size='small'
            required={!hasCoordinates}
            fullWidth
            value={Street ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                Street: target.value || null,
              }))
            }
          />
        </Grid>
        <Grid item sm={5}>
          <TextField
            label='Adresszusatz'
            size='small'
            fullWidth
            value={StreetAditional ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                StreetAditional: target.value || null,
              }))
            }
          />
        </Grid>
        <Grid item sm={2}>
          <TextField
            label='Hausnummer'
            size='small'
            required={!hasCoordinates}
            fullWidth
            value={StreetNr ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                StreetNr: target.value || null,
              }))
            }
          />
        </Grid>
        <Grid item sm={12}>
          <PostcodeSearch
            key={JSON.stringify(postcodeObject)} // Need to trigger re-render to show updated values
            postcodeObject={postcodeObject}
            required={!hasCoordinates}
            updatePostcodeObject={newValue =>
              setFormValues(x => ({
                ...x,
                idPostcode: newValue?.idPostcode ?? null,
                Postcode: newValue?.Postcode ?? undefined,
                City: newValue?.City || undefined,
              }))
            }
          />
        </Grid>
        <Grid item sm={12}>
          <Box sx={{ mt: 1 }} />
        </Grid>
        <Grid item sm={6}>
          <TextField
            label='Breitengrad'
            size='small'
            required={hasCoordinates}
            fullWidth
            value={Latitude ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                Latitude: Number(target.value) || null,
              }))
            }
          />
        </Grid>
        <Grid item sm={6}>
          <TextField
            label='Längengrad'
            size='small'
            required={hasCoordinates}
            fullWidth
            value={Longitude ?? ''}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues(x => ({
                ...x,
                Longitude: Number(target.value) || null,
              }))
            }
          />
        </Grid>
        <Grid item sm={12}>
          <Box sx={{ mt: 1 }} />
        </Grid>
        <Grid item sm={12}>
          <Accordion>
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              <Typography>Adresse / Koordinaten suchen</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isLoadingPostcodes ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <MapSearch
                  options={postcodes}
                  setMapSearch={newValue =>
                    setFormValues(x => ({ ...x, ...newValue }))
                  }
                />
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </>
  );
};
